// nls-rework\src\app\components\loan\auto-pay-status\auto-pay-status.component.ts
import { Component, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom, Subject, merge, map, withLatestFrom, switchMap, tap } from 'rxjs';
import { LoanDetail } from '../../../models/loan.model';
import { PaymentService } from '../../../services/payment.service';
import { LoanService } from '../../../services/loan.service';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../shared/confirmation-dialog/confirmation-dialog.component';

/**
 * Component that displays the auto pay status and controls for a loan.
 * Shows current auto pay state (ON/OFF) and provides actions to setup, edit, or cancel auto pay.
 */
@Component({
  selector: 'app-auto-pay-status',
  templateUrl: './auto-pay-status.component.html',
  styleUrl: './auto-pay-status.component.scss',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule
  ]
})
export class AutoPayStatusComponent {
  /** Service dependencies */
  readonly #router = inject(Router);
  readonly #paymentService = inject(PaymentService);
  readonly #loanService = inject(LoanService);
  readonly #dialog = inject(MatDialog);
  readonly #snackBar = inject(MatSnackBar);

  /** Subject to trigger a refresh of the loan details (internal) */
  readonly #refreshLoan$ = new Subject<void>();

  /** The loan ID input */
  readonly loanId = input.required<string>();

  /** Optional external refresh trigger from parent (bump the number to cause a reload) */
  readonly refreshTick = input<number>(0);

  /** Observables of inputs to react to changes */
  readonly #loanId$ = toObservable(this.loanId);
  readonly #refreshTick$ = toObservable(this.refreshTick);

  /** Signal containing the loan details */
  readonly loan = toSignal<LoanDetail | null>(
    merge(
      // Initial + subsequent loanId changes
      this.#loanId$,

      // Internal refreshes after we do actions in this component
      this.#refreshLoan$.pipe(
        withLatestFrom(this.#loanId$),
        map(([, id]) => id)
      ),

      // External refresh trigger from parent components (e.g., after canceling from the table)
      this.#refreshTick$.pipe(
        withLatestFrom(this.#loanId$),
        map(([, id]) => id)
      )
    ).pipe(
      switchMap(id => this.#loanService.getLoanDetails(id)),
      tap(() => { this.isCancelingAutoPay.set(false); })
    ),
    { initialValue: null }
  );

  /** Loading state for cancel auto pay operation */
  readonly isCancelingAutoPay = signal(false);

  /**
   * Handle setup auto pay action
   */
  onSetupAutoPay(): void {
    this.#navigateToPaymentWithAutoPay();
  }

  /**
   * Handle edit auto pay action
   */
  onEditAutoPay(): void {
    this.#navigateToPaymentWithAutoPay();
  }

  /**
   * Handle cancel auto pay action
   */
  async onCancelAutoPay(): Promise<void> {
    const dialogData: ConfirmationDialogData = {
      title: 'Cancel auto pay',
      message: 'Are you sure you want to cancel your automatic payment setup? This action cannot be undone.',
      confirmText: 'Cancel auto pay',
      cancelText: 'Keep auto pay',
      isDestructive: true
    };

    const dialogRef = this.#dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
      width: '400px',
      disableClose: true
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) return;

    const loan = this.loan();
    if (!loan?.recurringPayment?.id) return;

    this.isCancelingAutoPay.set(true);

    try {
      await firstValueFrom(this.#paymentService.cancelRecurringPayment(loan.recurringPayment.id));
      this.#refreshLoan$.next();

      this.#snackBar.open('Auto pay canceled successfully', 'Close', {
        duration: 3000,
        panelClass: ['snack-bar-success']
      });
    } catch (error) {
      console.error('Error canceling auto pay:', error);
      this.#snackBar.open('Error canceling auto pay. Please try again later.', 'Close', {
        duration: 0,
        panelClass: ['snack-bar-error']
      });
      this.isCancelingAutoPay.set(false);
    }
  }

  /**
   * Navigate to payment page with auto pay setup enabled
   */
  #navigateToPaymentWithAutoPay(): void {
    const loanId = this.loanId();
    this.#router.navigate(['/loan', loanId, 'payment'], {
      queryParams: { autoPay: 'true' }
    });
  }
}
